import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Optional, Output } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { InputValidationResult } from '@bl/components/dist/input/types';
import { TextInputProps } from '@bl/components/dist/text-input/types';
import { BLSvelteComponentWithValidation } from '@bl/components/dist/wrappers/wrappers';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { BlFrontendBaseComponent } from './bl-frontend-base.component';

const generateUniqueNameId = (() => {
  let id = 0;
  return () => {
    id += 1;
    return `bl-frontend-text-input-name-${id}`;
  };
})();

@Component({
  selector: 'bl-frontend-text-input',
  template: ``,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlFrontendTextInputComponent
  extends BlFrontendBaseComponent<TextInputProps>
  implements ControlValueAccessor
{
  @Input() value: TextInputProps['value'] = '';
  @Input() placeholder: TextInputProps['placeholder'] = '';
  @Input() maxlength: TextInputProps['maxlength'];
  @Input() minlength: TextInputProps['minlength'] = 0;
  @Input() pattern: TextInputProps['pattern'] = null;
  @Input() message: InputValidationResult['message'] = '';
  @Input() leftIcon: TextInputProps['leftIcon'] = null;
  @Input() rightIcon: TextInputProps['rightIcon'] = null;
  @Input() floatingText: TextInputProps['floatingText'] = false;
  @Input() clearable: TextInputProps['clearable'] = null;
  @Input() debounced: TextInputProps['debounced'] = null;
  @Input() description: TextInputProps['description'] = '';
  @Input() disabled: TextInputProps['disabled'] = false;
  @Input() text: TextInputProps['text'] = '';
  @Input() name: TextInputProps['name'] = generateUniqueNameId();
  @Input() onValidate: TextInputProps['onValidate'] = null;
  @Input() updateTrigger: TextInputProps['updateTrigger'] = 'input';
  @Input() focus: TextInputProps['focus'] = false;

  @Output() blurEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() keyDownEvent: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() validationChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private statusChangesSubscription: Subscription;
  constructor(
    protected override ref: ElementRef,
    @Optional() private ngControl: NgControl,
  ) {
    super(ref);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  protected override onInitialized = (): void => {
    this.statusChangesSubscription = this.ngControl?.statusChanges?.pipe(distinctUntilChanged()).subscribe(() => {
      (this.instance as BLSvelteComponentWithValidation<TextInputProps, unknown>).validate();
    });
  };

  protected override onDestroyed = (): void => {
    this.statusChangesSubscription?.unsubscribe();
  };

  private onChange: (value: unknown) => void;
  private onTouched: () => void;

  writeValue(obj: any): void {
    this.value = obj;
    this.updateProps({ value: obj });
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.updateProps({ disabled: isDisabled });
  }

  protected getComponentName(): string {
    return 'TextInput';
  }

  private validateInput(value: string): InputValidationResult {
    if (!this.onValidate && !this.pattern) {
      return { valid: true, message: '' };
    }

    if (this.onValidate) {
      return this.onValidate(value);
    }

    const regex = new RegExp(this.pattern);
    const isValid = regex.test(value);
    return {
      valid: isValid,
      message: isValid ? '' : this.message,
    };
  }

  protected getProps(): TextInputProps {
    const props: TextInputProps = {
      value: this.value,
      clearable: this.clearable,
      debounced: this.debounced,
      description: this.description,
      disabled: this.disabled,
      leftIcon: this.leftIcon,
      maxlength: this.maxlength,
      minlength: this.minlength,
      name: this.name,
      pattern: this.pattern,
      placeholder: this.placeholder,
      rightIcon: this.rightIcon,
      text: this.text,
      floatingText: this.floatingText,
      updateTrigger: this.updateTrigger,
      focus: this.focus,
      onBlur: (e: string): void => {
        this.blurEvent.emit(e);
        this.onTouched?.();
      },
      onChange: (value: string) => {
        this.value = value;
        this.valueChange.emit(value);
        this.onChange?.(value);
        const validationResult = this.validateInput(value);
        this.validationChange.emit(validationResult.valid);
      },
      onKeyDown: (e: KeyboardEvent): void => {
        this.keyDownEvent.emit(e);
      },
    };

    if (this.onValidate || this.pattern) {
      props.onValidate = this.validateInput.bind(this);
    }

    return props;
  }
}
